import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/image/logo1.png";

const PreviousCashReportPDF = forwardRef((props, ref) => {
  const docRef = useRef(null);

  useImperativeHandle(ref, () => ({
    generatePDF: (data, reportType) => {
      if (!data) {
        console.error("No data provided for PDF generation");
        return;
      }
      generatePDFDocument(data, reportType);
    }
  }));

  const generatePDFDocument = (data, reportType) => {
    const doc = new jsPDF();
    docRef.current = doc;

    // Add Logo
    if (logo) {
      try {
        doc.addImage(logo, "PNG", 15, 10, 35, 20);
      } catch (error) {
        console.warn("Could not load logo:", error);
      }
    }

    // Title based on report type
    const title = reportType === 'previous_day' 
      ? "PREVIOUS DAY CASH REPORT" 
      : "PREVIOUS MONTH CASH REPORT";
    
    doc.setFontSize(20);
    doc.text(title, 105, 20, { align: "center" });

    // Date/Period
    doc.setFontSize(11);
    if (reportType === 'previous_day') {
      doc.text(`Date: ${data.date}`, 15, 40);
    } else {
      doc.text(`Month: ${data.date}`, 15, 40);
    }
    doc.text(`Generated At: ${new Date().toLocaleTimeString()}`, 15, 47);

    // Summary Table
    autoTable(doc, {
      startY: 55,
      head: [["Application Type", "Fee (₹)", "Count", "Total Amount (₹)"]],
      body: [
        ["Zone Certificate", "200", data.zone.count, data.zone.count * 200],
        ["Part Plan", "700", data.partplan.count, data.partplan.count * 700],
        [
          "GRAND TOTAL",
          "",
          data.zone.count + data.partplan.count,
          data.zone.count * 200 + data.partplan.count * 700,
        ],
      ],
      theme: "grid",
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: 15, right: 15 },
    });

    // Detailed Zone Records
    if (data.zone.records && data.zone.records.length > 0) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [["Sr.No", "Application ID", "Applicant Name", "Amount (₹)", "Status"]],
        body: data.zone.records.map((row, index) => [
          index + 1,
          row.appId || row.request_unique_id || `ZON${String(index + 1).padStart(3, '0')}`,
          row.name || row.applicantName || "N/A",
          "700",
          row.status || "N/A"
        ]),
        theme: "grid",
        headStyles: { fillColor: [39, 174, 96] },
        margin: { left: 15, right: 15 },
      });
    }

    // Detailed Part Plan Records
    if (data.partplan.records && data.partplan.records.length > 0) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + (data.zone.records && data.zone.records.length > 0 ? 10 : 0),
        head: [["Sr.No", "Application ID", "Applicant Name", "Amount (₹)", "Status"]],
        body: data.partplan.records.map((row, index) => [
          index + 1,
          row.appId || row.request_unique_id || `PP${String(index + 1).padStart(3, '0')}`,
          row.name || row.applicantName || "N/A",
          "200",
          row.status || "N/A"
        ]),
        theme: "grid",
        headStyles: { fillColor: [142, 68, 173] },
        margin: { left: 15, right: 15 },
      });
    }

    // Payment Mode Summary
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [["Mode", "Count", "Amount (₹)"]],
      body: [
        ["Online", data.payment.online.count, data.payment.online.amount],
        ["Cash", data.payment.cash.count, data.payment.cash.amount],
        [
          "Total",
          data.payment.online.count + data.payment.cash.count,
          data.payment.online.amount + data.payment.cash.amount,
        ],
      ],
      theme: "grid",
      headStyles: { fillColor: [243, 156, 18] },
      margin: { left: 15, right: 15 },
    });

    // Remarks
    const remarksY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(11);
    doc.text("Remarks:", 15, remarksY);
    
    // Split long remarks into multiple lines
    const remarksLines = doc.splitTextToSize(data.remarks || "No remarks", 180);
    doc.text(remarksLines, 15, remarksY + 8);

    // Footer
    doc.setFontSize(9);
    doc.text("© 2025 Sangli Mahanagarpalika - Cash Report", 105, 285, {
      align: "center",
    });

    // File name
    const reportTypePrefix = reportType === 'previous_day' ? 'Previous_Day' : 'Previous_Month';
    const fileName = `${reportTypePrefix}_Cash_Report_${data.date ? data.date.replace(/\//g, '-').replace(/ /g, '_') : new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  return null;
});

export default PreviousCashReportPDF;